import React, { cloneElement, useCallback, useEffect, useMemo, useState } from 'react';

import { GRID_MIN_CELL_WIDTH } from '../../../constants';
import { ColumnElement } from '../columns/types';
import { SORT_DIRECTION } from '../enums';
import {
  calculateDepth,
  filterHideColumns,
  flattenChildrenColumns,
  isColumnElement,
  isValidReactElement,
  reconstructChildren,
  sortDates,
  sortNumbers,
  sortStrings,
} from '../utils';

import { GridStructure, GridStructureProps } from './types';

export function useGridStructure<T>({ children, rows }: GridStructureProps<T>): GridStructure<T> {
  const [columns, setColumns] = useState<ColumnElement[]>(filterHideColumns(children));
  const [column_widths_resized, setColumnWidths] = useState<number[]>([]);
  const [column_filters, setColumnFilters] = useState<string[]>([]);

  useEffect(() => {
    const columns_filtered = filterHideColumns(children);
    const columns_mapped = new Map(columns.map((col) => [col.key, col]));

    setColumns(() => {
      return columns_filtered.map((column) => {
        const col = columns_mapped.get(column.key);
        if (col) {
          return cloneElement(column, {
            ...column,
            width: col.props.width,
            sort_direction: col.props.sort_direction,
            fixed: col.props.fixed,
          });
        }

        return column;
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);

  const resizeColumn = useCallback(
    (index: number, width: number) => {
      setColumnWidths((prev) => {
        const widths = [...prev];
        widths[index] = Math.max(width, GRID_MIN_CELL_WIDTH);
        return widths;
      });
    },
    [setColumnWidths]
  );

  const filterColumn = useCallback(
    (index: number, filter: string) => {
      setColumnFilters((prev) => {
        const filters = [...prev];
        filters[index] = filter;
        return filters;
      });
    },
    [setColumnFilters]
  );

  const setColumnSortDirection = useCallback(
    (index: number, sort_direction: SORT_DIRECTION | null) => {
      setColumns((prev) => {
        const flatten_columns = flattenChildrenColumns(prev);

        // Reset sort_direction to null for all columns
        flatten_columns.forEach((col, i) => {
          flatten_columns[i] = React.cloneElement(col, { ...col.props, sort_direction: null });
        });

        if (index !== -1) {
          flatten_columns[index] = React.cloneElement(flatten_columns[index], {
            ...flatten_columns[index].props,
            sort_direction,
          });
        }

        return reconstructChildren(prev, flatten_columns);
      });
    },
    [setColumns]
  );

  const updateColumnSortDirection = React.useCallback(
    (index: number, sort_direction: SORT_DIRECTION | null) => {
      let column_sort_direction: SORT_DIRECTION | null;

      switch (sort_direction) {
        case SORT_DIRECTION.ASC:
          column_sort_direction = null;
          break;
        case SORT_DIRECTION.DESC:
          column_sort_direction = SORT_DIRECTION.ASC;
          break;
        default:
          column_sort_direction = SORT_DIRECTION.DESC;
          break;
      }

      return setColumnSortDirection(index, column_sort_direction);
    },
    [setColumnSortDirection]
  );

  const moveColumn = useCallback(
    (drag_index: number, hover_index: number) => {
      setColumns((prev) => {
        const flatten_columns = flattenChildrenColumns(prev);

        const [drag_column, hover_column] = [
          flatten_columns[drag_index],
          flatten_columns[hover_index],
        ];

        if (!drag_column.props.fixed && hover_column.props.fixed) {
          return prev;
        }

        if (drag_column.props.fixed) {
          if (drag_column.props.fixed === 'left') {
            if (hover_column.props.fixed !== 'left') {
              return prev;
            }
          }

          if (drag_column.props.fixed === 'right') {
            if (hover_column.props.fixed !== 'right') {
              return prev;
            }
          }
        }

        const [removed] = flatten_columns.splice(drag_index, 1);
        flatten_columns.splice(hover_index, 0, removed);

        setColumnWidths((prev_widths) => {
          const widths = [...prev_widths];
          const [removed] = widths.splice(drag_index, 1);
          widths.splice(hover_index, 0, removed);
          return widths;
        });

        return reconstructChildren(prev, flatten_columns);
      });
    },
    [setColumns]
  );

  const compareValues = (a: any, b: any, sort_direction: SORT_DIRECTION): number => {
    if (a === undefined || a === null) {
      return sort_direction === SORT_DIRECTION.ASC ? -1 : b === undefined || b === null ? 0 : 1;
    }
    if (b === undefined || b === null) {
      return sort_direction === SORT_DIRECTION.ASC ? 1 : -1;
    }

    if (a instanceof Date && b instanceof Date) {
      return sortDates(a, b);
    }
    if (typeof a === 'number' && typeof b === 'number') {
      return sortNumbers(a, b);
    }
    return sortStrings(String(a).toLowerCase(), String(b).toLowerCase());
  };

  const sortRows = useCallback((columns: ColumnElement[], rows: T[]): T[] => {
    const columns_sortable = columns
      .filter((column) => column.props.sort_field && column.props.sort_direction !== null)
      .sort((a, b) => (a.props.sort_priority || 0) - (b.props.sort_priority || 0));

    if (columns_sortable.length === 0) return rows;

    return [...rows].sort((a, b) => {
      for (const column of columns_sortable) {
        const { sort_field, sort_direction } = column.props;
        if (sort_field && sort_direction) {
          const valueA = a[sort_field as keyof T];
          const valueB = b[sort_field as keyof T];
          const result = compareValues(valueA, valueB, sort_direction as SORT_DIRECTION);
          if (result !== 0) {
            return sort_direction === SORT_DIRECTION.DESC ? -result : result;
          }
        }
      }
      return 0;
    });
  }, []);

  const filterRows = useCallback(
    (columns: ColumnElement[]): T[] => {
      const columns_filterable = columns
        .map((column, index) => {
          return { column, index };
        })
        .filter(({ column }) => column.props.filter_field);

      if (columns_filterable.length === 0) return rows;

      return [...rows].filter((row) => {
        for (const { column, index } of columns_filterable) {
          const value = row[column.props.filter_field as keyof T];
          if (!`${value}`.toLowerCase().includes(column_filters[index]?.toLowerCase() ?? ''))
            return false;
        }
        return true;
      });
    },
    [column_filters, rows]
  );

  const flattenColumnHeaders = useCallback((columns: ColumnElement[]): ColumnElement[] => {
    return columns.reduce((acc: ColumnElement[], child) => {
      if (isColumnElement(child)) {
        const column_children = React.Children.toArray(child.props.children);
        const columns_nested = column_children.filter(isColumnElement);

        if (columns_nested.length > 0) {
          // flatten columns to look for nested columns
          const columns_flatten = flattenColumnHeaders(columns_nested);
          acc.push(cloneElement(child, { ...child.props, children: columns_flatten }));
        } else {
          // remove children which represent the cells
          acc.push(cloneElement(child, { ...child.props, children: undefined }));
        }
      }
      return acc;
    }, []);
  }, []);

  const flattenColumnCells = useCallback((columns: ColumnElement[]): ColumnElement[] => {
    return columns.reduce((acc: ColumnElement[], column) => {
      if (isColumnElement(column)) {
        const column_children = React.Children.toArray(column.props.children);
        const columns_nested = column_children.filter(isColumnElement);
        const cells_nested = column_children.filter(isValidReactElement);

        if (columns_nested.length > 0) {
          // flatten columns to look for nested cells
          const columns_flatten = flattenColumnCells(columns_nested);

          // merge the properties of the parent column with each child column
          const columns_merged = columns_flatten.map((column_nested) =>
            cloneElement(column_nested, {
              ...column_nested.props,
              fixed: column.props.fixed || column_nested.props.fixed,
            })
          );
          acc.push(...columns_merged);
        } else {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          acc.push(cloneElement(column, { ...column.props, children: cells_nested }));
        }
      }
      return acc;
    }, []);
  }, []);

  const columns_cells = useMemo(() => flattenColumnCells(columns), [columns, flattenColumnCells]);
  const columns_headers = useMemo(
    () => flattenColumnHeaders(columns),
    [columns, flattenColumnHeaders]
  );
  const headers_depth = useMemo(() => calculateDepth(columns_headers), [columns_headers]);
  const rows_filtered = useMemo(() => filterRows(columns_cells), [columns_cells, filterRows]);
  const rows_sorted = useMemo(
    () => sortRows(columns_cells, rows_filtered),
    [columns_cells, rows_filtered, sortRows]
  );

  return {
    rows_sorted,
    headers: columns_headers,
    columns: columns_cells,
    depth: headers_depth,
    column_widths_resized,
    column_filters,
    resizeColumn,
    filterColumn,
    setColumns,
    moveColumn,
    setColumnSortDirection,
    updateColumnSortDirection,
  };
}
